'use client'

// View Project (untuk tech lead & supervisor): tiap project — mulai → target selesai,
// progress keseluruhan, on-pace, dan PROGRESS MINGGUAN.
import { useState } from 'react'
import { ChevronDown, ExternalLink, CalendarClock, Plus } from 'lucide-react'
import type { Task } from '@/lib/types'
import { projectProgress, isoDay } from '@/lib/dev-sprint'
import { useToast } from '@/context/ToastContext'
import type { BoardData, Filters, DevDerived } from './types'
import { StatusPill } from './ui'

const fmt = (iso: string | null) => (iso ? new Date(iso + 'T00:00:00').toLocaleDateString('id-ID', { day: 'numeric', month: 'short', year: 'numeric' }) : '—')

interface Props {
  board: BoardData
  derived: DevDerived
  filters: Filters
  today: string
  isLead: boolean
  onOpenTask: (id: string) => void
  onAddTask: (projectId: string) => void
  onChanged: () => void
}

export function ProjectsView({ board, derived, filters, today, isLead, onOpenTask, onAddTask, onChanged }: Props) {
  const { statusOf, projById } = derived
  const { toast } = useToast()
  const [openId, setOpenId] = useState<string | null>(null)

  const saveDates = async (id: string, patch: Record<string, unknown>) => {
    try {
      const res = await fetch(`/api/dev/projects/${id}`, { method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(patch) })
      if (!res.ok) throw new Error((await res.json().catch(() => ({}))).error || 'Gagal menyimpan')
      toast('Project diperbarui', 'success')
      onChanged()
    } catch (e) {
      toast(e instanceof Error ? e.message : 'Gagal', 'error')
    }
  }

  const projects = filters.project ? board.projects.filter((p) => p.id === filters.project) : board.projects
  if (projects.length === 0) {
    return <div className="rounded-2xl border border-[#E7E5DF] bg-white py-16 text-center text-[13px] text-[#AEADA8]">Belum ada project tim.</div>
  }

  return (
    <div className="space-y-3">
      {projects.map((p) => {
        const tasks = board.tasks.filter((t) => t.projectId === p.id)
        const pr = projectProgress(p, tasks, statusOf, today)
        const open = openId === p.id
        const maxWk = Math.max(1, ...pr.weeks.map((w) => w.donePts))
        const paceBadge = pr.onPace == null
          ? { label: 'Tanpa target', bg: '#F1F1EF', text: '#6B6A65' }
          : pr.onPace
          ? { label: 'On pace', bg: '#E4F5EA', text: '#1E7A46' }
          : { label: 'Behind', bg: '#FCE8E8', text: '#C0362C' }
        return (
          <div key={p.id} className="rounded-2xl border border-[#E7E5DF] bg-white overflow-hidden">
            <button onClick={() => setOpenId(open ? null : p.id)} className="w-full text-left p-4 hover:bg-[#FAFAF8] transition-colors">
              <div className="flex items-center gap-2.5 mb-2">
                <span className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: p.color }} />
                <span className="text-[15px] font-bold text-[#1A1916] truncate flex-1">{p.name}</span>
                {p.pic && <span className="text-[11px] text-[#8B8A85] hidden sm:block">PIC: {p.pic}</span>}
                <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full" style={{ backgroundColor: paceBadge.bg, color: paceBadge.text }}>{paceBadge.label}</span>
                <ChevronDown className={`w-4 h-4 text-[#AEADA8] transition-transform ${open ? 'rotate-180' : ''}`} />
              </div>

              <div className="flex items-center gap-2 text-[12px] text-[#8B8A85] mb-2.5">
                <CalendarClock className="w-3.5 h-3.5" />
                <span className="tabular-nums">Mulai {fmt(pr.start)} → Target {fmt(pr.finish)}</span>
                {pr.elapsedPct != null && <span className="text-[#AEADA8]">· {pr.elapsedPct}% waktu berlalu</span>}
              </div>

              {/* Progress bar */}
              <div className="flex items-center gap-3">
                <div className="flex-1 h-2 rounded-full bg-[#F0EEE7] overflow-hidden">
                  <div className="h-full rounded-full" style={{ width: `${pr.pct}%`, backgroundColor: p.color }} />
                </div>
                <span className="text-[13px] font-bold text-[#1A1916] tabular-nums w-10 text-right">{pr.pct}%</span>
              </div>
              <div className="flex items-center gap-3 mt-1.5 text-[11px] text-[#8B8A85] tabular-nums">
                <span>{pr.tasksDone}/{pr.tasksTotal} task</span>
                <span>· {pr.donePts}/{pr.totalPts} pts</span>
                {pr.openUndated > 0 && <span className="text-[#C2410C]">· {pr.openUndated} tanpa deadline</span>}
              </div>

              {/* Progress mingguan (mini bars) */}
              <div className="mt-3">
                <div className="flex items-end gap-1 h-12">
                  {pr.weeks.map((w) => (
                    <div key={w.week} className="flex-1 flex flex-col items-center justify-end gap-1 min-w-0" title={`Minggu ${w.week} (${w.label}): +${w.donePts} pts · kumulatif ${w.cumPct}%`}>
                      <div className="w-full rounded-sm" style={{ height: `${Math.max(4, (w.donePts / maxWk) * 100)}%`, backgroundColor: w.donePts ? p.color : '#EDEBE5' }} />
                    </div>
                  ))}
                </div>
                <div className="flex gap-1 mt-1">
                  {pr.weeks.map((w) => (
                    <div key={w.week} className="flex-1 text-center text-[9px] text-[#AEADA8] tabular-nums min-w-0 truncate">W{w.week}</div>
                  ))}
                </div>
              </div>
            </button>

            {/* Detail expand: tabel mingguan + task */}
            {open && (
              <div className="border-t border-[#EDEBE5] p-4 bg-[#FBFAF7]">
                {isLead && (
                  <div className="flex flex-wrap items-end gap-3 mb-3 pb-3 border-b border-[#F0EEE7]">
                    <label className="text-[11px] font-semibold text-[#8B8A85]">Mulai
                      <input type="date" defaultValue={isoDay(p.startDate) || isoDay(p.createdAt)} onChange={(e) => saveDates(p.id, { startDate: e.target.value || null })} className="block mt-1 bg-white border border-[#E7E5DF] rounded-lg px-2 py-1.5 text-[13px] text-[#1A1916] focus:outline-none focus:ring-2 focus:ring-[#D4A843]/40" />
                    </label>
                    <label className="text-[11px] font-semibold text-[#8B8A85]">Target selesai
                      <input type="date" defaultValue={isoDay(p.deadline)} onChange={(e) => saveDates(p.id, { deadline: e.target.value || null })} className="block mt-1 bg-white border border-[#E7E5DF] rounded-lg px-2 py-1.5 text-[13px] text-[#1A1916] focus:outline-none focus:ring-2 focus:ring-[#D4A843]/40" />
                    </label>
                    <button onClick={() => onAddTask(p.id)} className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl text-[13px] font-semibold text-white bg-[#1A1916] hover:bg-black transition-colors">
                      <Plus className="w-4 h-4" /> Task
                    </button>
                  </div>
                )}
                <div className="flex items-center justify-between mb-2">
                  <h4 className="text-[12px] font-bold text-[#1A1916]">Progress Mingguan</h4>
                  <a href={`/projects/${p.id}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-[11px] text-[#8B8A85] hover:text-[#1A1916]">Buka project <ExternalLink className="w-3 h-3" /></a>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-[12px] mb-3">
                    <thead>
                      <tr className="text-[#AEADA8] text-left">
                        <th className="font-semibold py-1 pr-3">Minggu</th>
                        <th className="font-semibold py-1 pr-3">Mulai</th>
                        <th className="font-semibold py-1 pr-3">Points selesai</th>
                        <th className="font-semibold py-1">Kumulatif</th>
                      </tr>
                    </thead>
                    <tbody>
                      {pr.weeks.map((w) => (
                        <tr key={w.week} className="border-t border-[#F0EEE7]">
                          <td className="py-1 pr-3 font-semibold text-[#1A1916]">W{w.week}</td>
                          <td className="py-1 pr-3 text-[#6B6A65] tabular-nums">{w.label}</td>
                          <td className="py-1 pr-3 tabular-nums">{w.donePts}</td>
                          <td className="py-1 tabular-nums">{w.cumPct}%</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {/* Task open project ini */}
                <h4 className="text-[12px] font-bold text-[#1A1916] mb-1.5">Task berjalan</h4>
                {tasks.filter((t) => statusOf(t) !== 'done').length === 0 ? (
                  <p className="text-[12px] text-[#AEADA8]">Semua task selesai 🎉</p>
                ) : (
                  <ul className="space-y-1">
                    {tasks.filter((t) => statusOf(t) !== 'done').slice(0, 8).map((t) => (
                      <li key={t.id}>
                        <button onClick={() => onOpenTask(t.id)} className="w-full flex items-center gap-2 py-1 px-1.5 -mx-1.5 rounded-lg hover:bg-white text-left transition-colors">
                          <span className="text-[12px] text-[#4A4844] truncate flex-1">{t.title}</span>
                          <span className="text-[11px] text-[#AEADA8]">{t.assignee || '—'}</span>
                          <span className="text-[11px] text-[#AEADA8] tabular-nums">{isoDay(t.dueDate) ? isoDay(t.dueDate).slice(5) : '—'}</span>
                          <StatusPill status={statusOf(t)} />
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}
          </div>
        )
      })}
    </div>
  )
}
